(() => {
  if (window.cutimArticleFaq) return;

  const text = (node) => {
    if (!node) return '';
    const clone = node.cloneNode(true);
    clone.querySelectorAll('script, style, [hidden], [aria-hidden="true"]').forEach((element) => element.remove());
    clone.querySelectorAll('br').forEach((element) => element.replaceWith(' '));
    clone.querySelectorAll('p, li, div').forEach((element) => element.append(' '));
    return clone.textContent.replace(/\s+/g, ' ').trim();
  };
  const questionKey = (value) => value.replace(/^\s*(?:\d+[.)]\s*|Q\s*:\s*)/i, '').toLowerCase().replace(/\s+/g, ' ').trim();
  const isQuestion = (value) => /\?\s*$/.test(value) || /^(?:\d+[.)]\s*)?(?:Q\s*:|who\b|what\b|when\b|where\b|why\b|how\b|which\b|can\b|could\b|should\b|would\b|will\b|do\b|does\b|did\b|is\b|are\b)/i.test(value);
  const isFaqTitle = (value) => /^(?:\d+[.)]\s*)?(?:frequently asked questions|faqs?)(?:\b|\s|:)/i.test(value);
  const hasFaqType = (value) => {
    if (!value || typeof value !== 'object') return false;
    if ([value['@type']].flat().some((type) => /(?:^|\/)FAQPage$/.test(type || ''))) return true;
    return Object.values(value).some((entry) => Array.isArray(entry) ? entry.some(hasFaqType) : hasFaqType(entry));
  };

  function readPairs(nodes) {
    const pairs = [];
    let question = '';
    let answers = [];
    let waitingForAnswer = false;
    const flush = () => {
      const answer = answers.filter(Boolean).join('\n').trim();
      if (question && answer) pairs.push({ question, answer });
      question = '';
      answers = [];
      waitingForAnswer = false;
    };
    for (const node of nodes) {
      if (node.nodeType !== Node.ELEMENT_NODE || node.matches('[hidden], [aria-hidden="true"]')) continue;
      if (node.matches('h1,h2')) {
        flush();
      } else if (node.matches('h3')) {
        flush();
        question = text(node);
      } else if (node.matches('details')) {
        flush();
        const summary = node.querySelector('summary');
        const clone = node.cloneNode(true);
        clone.querySelector('summary')?.remove();
        if (text(summary) && text(clone)) pairs.push({ question: text(summary), answer: text(clone) });
      } else if (node.matches('p') && /^Q\s*:/i.test(text(node))) {
        flush();
        const match = text(node).match(/^Q\s*:\s*(.+?)\s+A\s*:\s*([\s\S]+)$/i);
        if (match) pairs.push({ question: match[1].trim(), answer: match[2].trim() });
        else {
          question = text(node).replace(/^Q\s*:\s*/i, '').trim();
          waitingForAnswer = true;
        }
      } else if (waitingForAnswer) {
        if (node.matches('p') && /^A\s*:/i.test(text(node))) {
          answers.push(text(node).replace(/^A\s*:\s*/i, '').trim());
          waitingForAnswer = false;
        } else if (text(node)) flush();
      } else if (question && !node.matches('script, style, nav')) {
        answers.push(text(node));
      }
    }
    flush();
    return pairs;
  }

  function bodyPairs(content) {
    if (!content) return [];
    const regions = [];
    const explicit = [...content.querySelectorAll('[data-faq]')];
    explicit.forEach((region) => {
      if (!region.parentElement.closest('[data-faq]')) regions.push({ element: region, nodes: [...region.children] });
    });
    content.querySelectorAll('h2').forEach((heading) => {
      if (heading.closest('[data-faq]') || !isFaqTitle(text(heading))) return;
      const nodes = [];
      for (let node = heading.nextSibling; node; node = node.nextSibling) {
        if (node.nodeType === Node.ELEMENT_NODE && (node.matches('h1,h2') || (node.matches('h3') && !isQuestion(text(node))))) break;
        nodes.push(node);
      }
      regions.push({ heading, nodes });
    });
    return regions.flatMap((region) => {
      const pairs = readPairs(region.nodes);
      if (!pairs.length) return [];
      let section = region.element;
      if (!section) {
        section = document.createElement('section');
        region.heading.before(section);
        section.append(region.heading, ...region.nodes);
      }
      section.classList.add('cutim-article-faq');
      section.dataset.faq = '';
      const title = section.querySelector('h2');
      if (title) {
        if (!title.id) {
          let id = 'article-faq';
          while (document.getElementById(id)) id += '-section';
          title.id = id;
        }
        section.setAttribute('aria-labelledby', title.id);
      }
      return pairs;
    });
  }

  function initialize(root = document) {
    root.querySelectorAll('.cutim-article-body').forEach((body) => {
      if (body.dataset.faqReady) return;
      body.dataset.faqReady = 'true';
      const content = body.querySelector('.blog-post-content rte-formatter');
      const pairs = bodyPairs(content);
      const seen = new Set(pairs.map((pair) => questionKey(pair.question)));
      const legacy = body.querySelector('.cutim-legacy-faq');
      legacy?.querySelectorAll('[data-faq-item]').forEach((item) => {
        const question = text(item.querySelector('[data-faq-question]'));
        const answer = text(item.querySelector('[data-faq-answer]'));
        if (!question || !answer || seen.has(questionKey(question))) {
          item.remove();
          return;
        }
        seen.add(questionKey(question));
        pairs.push({ question, answer });
      });
      if (legacy && !legacy.querySelector('[data-faq-item]')) legacy.remove();

      const unique = [];
      const questions = new Set();
      pairs.forEach((pair) => {
        const key = questionKey(pair.question);
        if (!questions.has(key)) {
          questions.add(key);
          unique.push(pair);
        }
      });
      body.dataset.faqCount = String(unique.length);
      if (!unique.length) return;
      const alreadyMarked = document.querySelector('[itemtype$="/FAQPage"], [typeof~="FAQPage"]') || [...document.querySelectorAll('script[type="application/ld+json"]')].some((script) => {
        try { return hasFaqType(JSON.parse(script.textContent)); } catch { return false; }
      });
      if (alreadyMarked) return;
      const canonical = document.querySelector('link[rel="canonical"]')?.href || location.origin + location.pathname;
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'cutim-article-faq-schema';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': canonical + '#faq',
        url: canonical,
        mainEntity: unique.map(({ question, answer }) => ({
          '@type': 'Question', name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer }
        }))
      });
      body.append(script);
    });
  }

  window.cutimArticleFaq = { initialize };
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
